import type { ReactElement } from "react";
import { Link } from "react-router";
import { getRuntimeConfig } from "@/app/config/runtime-config";

const FOOTER_LINK_CLASSES =
  "text-[16px] font-semibold uppercase text-white no-underline transition-colors hover:text-gray-300 hover:no-underline";

const FOOTER_LINKS = [
  { label: "Privacy Policy", href: "/home/privacypolicy" },
  { label: "Terms of Service", href: "/home/termsofservice" },
  { label: "Antitrust Statement", href: "/home/antitruststatement" },
  { label: "Contact", href: "/home/contactus" },
];

// Tracker footer order: LinkedIn, Facebook, Instagram, X; targets from the legacy footer.
const FOOTER_SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/2766037?trk=vsrp_companies_res_name&trkInfo=VSRPsearchId%3A226769511476996422049%2CVSRPtargetId%3A2766037%2CVSRPcmpt%3Aprimary",
    icon: "/logos/logo-linkedin.svg",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/pages/Claims-and-Litigation-Management-Alliance/161320700587039",
    icon: "/logos/logo-facebook.svg",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/theclmalliance/",
    icon: "/logos/logo-instagram.svg",
  },
  {
    label: "X (formerly Twitter)",
    href: "https://x.com/theclmalliance",
    icon: "/logos/logo-x.svg",
  },
];

/**
 * Shared global footer (FR-040): three-column tracker port — social icons
 * left, links plus copyright center, brand logo right. Rendered by the
 * authenticated shell and by every public surface (login, /home/*).
 * The Wiki link resolves from runtime config (FR-041) — never hardcoded.
 * wikiBaseUrl is optional in supplied config and derived per environment
 * during parsing (explicit value wins; invalid supplied values reject), so
 * the parsed config always carries it and the link is always present.
 */
export function AppFooter(): ReactElement {
  const { wikiBaseUrl } = getRuntimeConfig();

  return (
    <footer className="bg-gradient-to-r from-[#1893D1] to-[#16456B]">
      <div className="clm-page-gutter flex flex-col items-center gap-4 py-[20px] text-white sm:flex-row sm:justify-between">
        <ul className="m-0 flex list-none items-center gap-[10px] p-0 sm:basis-[20%]">
          {FOOTER_SOCIAL_LINKS.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={link.label}
                className="flex h-[24px] w-[24px] items-center justify-center transition-opacity hover:opacity-75"
              >
                <img src={link.icon} alt="" className="h-[20px] w-[20px]" width={20} height={20} />
              </a>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-center gap-2">
          <nav aria-label="Footer">
            <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-[20px] p-0">
              {FOOTER_LINKS.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className={FOOTER_LINK_CLASSES}>
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={wikiBaseUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={FOOTER_LINK_CLASSES}
                >
                  Wiki
                </a>
              </li>
            </ul>
          </nav>
          {/* Tracker footer reads "Clams"; carrying the intended organization name instead. */}
          <span className="text-[15px] font-medium">
            © Claims and Litigation Management Alliance
          </span>
        </div>
        <div className="flex justify-end sm:basis-[20%]">
          <img
            src="/logos/footer-logo.svg"
            alt="Claims and Litigation Management Alliance logo"
            className="h-[60px] w-auto"
            loading="lazy"
            width={130}
            height={60}
          />
        </div>
      </div>
    </footer>
  );
}
